<template>
  <div class="dayRangePicker">
    <div class="monthsHeader">
      {{getMonthsTitle()}}
    </div>
    <section class="dayBody">
      <header class="dayNames">
        <span class="dayLetter"
              v-for="(day, index) in daysAvailable"
              :key="index">
          {{day}}
        </span>
      </header>
      <section class="dayNumbers">
        <div class="dayRow"
             v-for="(row, index) in getRows()"
             :key="index">
          <span v-for="(day, index) in row"
                :class="getSelectableDateClass(day)"
                :key="index"
                @click="handleClick(day)"
                @keyup.enter="handleClick(day)"
                tabindex="0"
          >
            <span class="dayNumber">{{day.date.date()}}</span>
          </span>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  let dayRangePicker = {
    name: 'DayRangePicker',
    props: {
      daysAvailable: { // parameter for internationalization
        type: Array,
        default: function() {
          return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        }
      },
      availableHours: {
        type: Number,
        default: 720
      },
      value: {
        type: Object,
        default: function() {
          return {
            startDay: undefined,
            endDay: undefined
          };
        },
      },
      weekStart: { // for internationalization
        type: Number,
        default: 6 // defaults to Sunday, 0 for Monday
      },
    },
    watch: {
      value: function(value) {
          let startDay = value.startDay ? moment(value.startDay) : undefined;
          let endDay = value.endDay ? moment(value.endDay) : undefined;
          this.startDay = startDay;
          this.endDay = endDay;
        },
    },
    data: function () {
      let startDay = this.value.startDay ? moment(this.value.startDay) : undefined;
      let endDay = this.value.endDay ? moment(this.value.endDay) : undefined;
      return {
        startDay: startDay,
        endDay: endDay
      };
    },
    methods: {
      getMonthsTitle: function () {
        let now = moment();
        let oldestData = moment().subtract(this.availableHours, 'hours');
        let oldestMonth = oldestData.format('MMMM');
        let currentMonth = now.format('MMMM');
        if (currentMonth !== oldestMonth) {
          return oldestMonth + ' / ' + currentMonth;
        }
        return currentMonth;
      },
      getRows: function() {
        let numDays = Math.floor(this.availableHours / 24);
        let rows = [];
        let now = moment();
        // set to noon for precision
        now.hours(12);
        now.minutes(0);
        now.seconds(0);
        // add selectable days using less than or equal to to account for partial days
        for (let i = 0; i <= numDays; i++) {
          if (i === 0) {
            // init
            rows.push([{available: true, date: now}]);
          } else {
            // follow regular logic
            let dayAdding = moment(now).subtract(i, 'days');
            // add new row if start of week
            if (dayAdding.day() === this.weekStart) {
              rows.unshift([]);
            }
            // add to beginning of first row because we're going backwards
            rows[0].unshift({available: true, date: dayAdding});
          }
        }
        // now add padding days
        if (rows.length > 1) {
          if (rows[0].length < 7) {
            let firstRow = rows[0];
            let lastDate = moment(firstRow[0].date);
            // pre pad top row (too old data)
            for (let i = firstRow.length; i < 7; i++) {
              lastDate = moment(lastDate).subtract(1, 'days');
              firstRow.unshift({
                available: false,
                date: lastDate
              });
            }
          }
          if (rows[rows.length - 1].length < 7) {
            let lastRow = rows[rows.length - 1];
            let nextDate = moment(lastRow[lastRow.length - 1].date);
            // post pad bottom row (future dates)
            for (let i = lastRow.length; i < 7; i++) {
              nextDate = moment(nextDate).add(1, 'days');
              lastRow.push({
                available: false,
                date: nextDate
              });
            }
          }
        } else {
          // one row so we need to figure out how many go before vs after
          let row = rows[0];
          for (let i = row.length; i < 7; i++) {
            let firstDay = row[0].date;
            if (firstDay.day() != 0) {
              row.unshift({
                available: false,
                date: moment(firstDay).subtract(1, 'days')
              });
            } else {
              let lastDay = row[row.length - 1].date;
              row.push({
                available: false,
                date: moment(lastDay).add(1, 'days')
              });
            }
          }
        }
        return rows;
      },
      getSelectableDateClass: function(day) {
        let classString = 'selectableDate';
        if (this.startDay) {
          if (Math.abs(this.endDay.diff(this.startDay, 'hour')) < 3
              && Math.abs(day.date.diff(this.startDay, 'hour')) < 3) {
            classString += ' singleDate';
          } else if (Math.abs(day.date.diff(this.startDay, 'hour')) < 3) {
            classString += ' startDate';
          } else if (Math.abs(day.date.diff(this.endDay, 'hour')) < 3) {
            classString += ' endDate';
          } else if (day.date.isBetween(this.startDay, this.endDay)) {
            classString += ' middleDate';
          } else {
            if (day.available) {
              classString += ' available';
            } else {
              classString += ' unavailable';
            }
          }
        } else {
          if (day.available) {
            classString += ' available';
          } else {
            classString += ' unavailable';
          }
        }
        return classString;
      },
      /**
       * Handles a click on the day range picker.
       * 
       * @listens onclick, element
       */
      handleClick: function(day) {
        let date = day.date;
        if (day.available === false) {
          return;
        }
        let startDay = this.startDay;
        let endDay = this.endDay;
        if (startDay) {
          // already clicked
          if (date.isBefore(this.startDay)) {
            startDay = moment(date);
            endDay = moment(endDay);
          } else if (date.isAfter(endDay)) {
            startDay = moment(startDay);
            endDay = moment(date);
          } else {
            // clicked inbetween so reset
            startDay = moment(date);
            endDay = startDay;
          }
        } else {
          // first click
          startDay = moment(date);
          endDay = startDay;
        }
        // update local value first, then emit event
        this.startDay = startDay;
        this.endDay = endDay;
        /**
         * Value change event for this component.
         * 
         * @type {number}
         */
        this.$emit('day-range-change', {
          startDay: startDay,
          endDay: endDay
        });
      },
    }
  };
  export default dayRangePicker;
</script>

<style lang="scss" scope>
$headerColor: #757575;
$borderColor: #CECECE;
$circleColor: #ECF0F5;
$calendarWidth: 260px;
$selectedMiddleBackground: var(--disabled-background);
$sizeWithBorder: 30px;
$sizeWithoutBorder: 34px;
.dayRangePicker {
  .monthsHeader {
    color: $headerColor;
    font-weight: bold;
  }
  font-family: var(--font-family);
  font-size: 12px;
  text-align: center;
  line-height: 30px;

  .dayBody {
    display: flex;
    flex-direction: column;
    .dayNames {
      margin-left: auto;
      margin-right: auto;
      border-bottom: 1px solid $borderColor;
    }
    .dayNumbers {
      margin-left: auto;
      margin-right: auto;
      .dayRow {
        margin-top: 5px;
      }
    }
    span {
      display: inline-block;
      &.dayLetter, &.unavailable {
        width: 35px;
        padding-right: 4px;
        height: 35px;
      }
      &.selectableDate {
        color: $borderColor;
      }
      &.selectableDate.available {
        width: $sizeWithBorder;
        color: var(--text-color);
        border-radius: 50%;
        border: 2px solid $circleColor;
        cursor: pointer;
        margin-right: 5px;
        .dayNumber {
          width: $sizeWithBorder;
        }
        &:hover, &:focus {
          border: 2px solid var(--default-color);
        }
      }
      &.selectableDate.singleDate {
        width: $sizeWithBorder;
        height: $sizeWithBorder;
        background-color: var(--default-color);
        color: white;
        border: none;
        border-radius: 50%;
        margin-right: 7px;
        margin-left: 2px;
      }
      &.selectableDate.startDate {
        width: $sizeWithBorder;
        height: $sizeWithBorder;
        color: white;
        border: none;
        cursor: pointer;
        background: linear-gradient(90deg, var(--panel-background) 50%, $selectedMiddleBackground 50%);
        position: relative;
        z-index: 0;
        margin-left: 2px;
        padding-right: 7px;
        .dayNumber {
          position: relative;
          width: $sizeWithBorder;
          z-index: 3;
        }
        &::after {
          width: $sizeWithBorder;
          height: $sizeWithBorder;
          left: 0;
          top: 0;
          position: absolute;
          content: '';
          border-radius: 50%;
          background-color: var(--default-color);
          z-index: 2;
        }
      }
      &.selectableDate.endDate {
        width: $sizeWithBorder;
        height: $sizeWithBorder;
        color: white;
        border: none;
        cursor: pointer;
        background: linear-gradient(90deg, $selectedMiddleBackground 50%, var(--panel-background) 50%);
        position: relative;
        z-index: 0;
        padding-left: 4px;
        margin-right: 5px;
        .dayNumber {
          position: relative;
          width: $sizeWithBorder;
          z-index: 3;
        }
        &::after {
          width: $sizeWithBorder;
          height: $sizeWithBorder;
          left: 4px;
          top: 0;
          position: absolute;
          content: '';
          border-radius: 50%;
          background-color: var(--default-color);
          z-index: 2;
        }
      }
      &.selectableDate.middleDate {
        width: 35px;
        color: var(--text-color);
        cursor: pointer;
        border-radius: 0;
        background-color: $selectedMiddleBackground;
        border: none;
        margin-top: 2px;
        margin-bottom: 2px;
        padding-right: 4px;
      }
    }
  }
}
</style>
